import axios, { type AxiosInstance, AxiosError, type InternalAxiosRequestConfig } from 'axios';
import {
  getStoredToken,
  tryRefreshToken,
  enqueueBackgroundTokenRefresh,
  setLogoutCallback as setTokenServiceLogoutCallback,
  setSessionRefreshedCallback as setTokenServiceSessionRefreshedCallback,
} from './tokenService';
import type { OAuthTokenResponse } from '../features/auth/types';
import { REFRESH_BUFFER_SECONDS, TOKEN_EXPIRY_KEY } from '../features/auth/constants';

const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || '',
  headers: {
    'Content-Type': 'application/json',
  },
});

export const setLogoutCallback = (callback: () => void) => {
  setTokenServiceLogoutCallback(callback);
};

export const setSessionRefreshedCallback = (
  callback: (tokenResponse: OAuthTokenResponse) => void
) => {
  setTokenServiceSessionRefreshedCallback(callback);
};

// Attach token (if present) and ensure single-flight refresh on expiry
api.interceptors.request.use(
  async (config: InternalAxiosRequestConfig) => {
    if (typeof FormData !== 'undefined' && config.data instanceof FormData && config.headers) {
      config.headers.delete?.('Content-Type');
      delete config.headers['Content-Type'];
    }

    const token = getStoredToken();
    const tokenExpiry = localStorage.getItem(TOKEN_EXPIRY_KEY);

    if (!token || !tokenExpiry) return config;

    const currentTime = Math.floor(Date.now() / 1000);
    const expiryTime = parseInt(tokenExpiry, 10);

    if (currentTime >= expiryTime) {
      // Expired — try to refresh (single-flight handled in tokenService)
      try {
        const newToken = await tryRefreshToken();
        if (config.headers) config.headers.Authorization = `Bearer ${newToken}`;
      } catch (err) {
        // tryRefreshToken already called logoutCallback and cleared storage
        return Promise.reject(err);
      }
      return config;
    }

    if (expiryTime - currentTime < REFRESH_BUFFER_SECONDS) {
      // Near expiry — refresh in background, still use current token
      enqueueBackgroundTokenRefresh();
    }

    if (config.headers) config.headers.Authorization = `Bearer ${token}`;
    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

// Response interceptor: try refresh on 401 and retry the original request once
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const newToken = await tryRefreshToken();
        if (originalRequest.headers) originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshErr) {
        // Refresh failure already clears storage and triggers logout callback in tokenService.
        return Promise.reject(refreshErr);
      }
    }

    return Promise.reject(error);
  }
);

export default api;
